import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { openModal } from 'actions/modals';
import { DESTINATION_WELCOME_TOUR_MODAL } from 'constants/modalTypes';
import {
  SHOW_FTUX_NOTIFICATIONS_TRANSCRIPT_TOOLTIP,
  SHOW_FTUX_POST_VIDEO_POPOVER,
  SHOW_FTUX_SHARE_VIDEO_TAG_TOOLTIP,
  SHOW_FTUX_WELCOME_MODAL,
} from 'constants/triggers';

import useFtux from './useFtux';

const launchDate = '2022-03-15';
const maxDuration = { weeks: 15 };

function useShouldShowOneTimeFtux(triggerKey: string): {
  showFtux: boolean;
  dismissFtuxOnClick: () => void;
} {
  const [showFtux, dismiss] = useFtux({
    triggerKey,
    launchDate,
    maxDuration,
  });
  const ftuxRef = useRef(showFtux);

  const dismissFtuxOnClick = () => {
    dismiss();
    ftuxRef.current = false;
  };

  useEffect(() => {
    if (showFtux) {
      dismiss();
    }
  });

  return { showFtux: ftuxRef.current, dismissFtuxOnClick };
}

export const useShowFtuxWelcomeModal = (): {
  dismissFtuxWelcomeModal: () => void;
} => {
  const dispatch = useDispatch();
  const [ftuxEnabled, dismissFtuxWelcomeModal] = useFtux({
    triggerKey: SHOW_FTUX_WELCOME_MODAL,
    launchDate: '2022-03-15',
    maxDuration: { weeks: 20 },
  });

  useEffect(() => {
    if (ftuxEnabled) {
      dispatch(openModal(DESTINATION_WELCOME_TOUR_MODAL));
    }
  }, [dispatch, ftuxEnabled]);

  return { dismissFtuxWelcomeModal };
};

export const useDismissDestinationFtuxTooltips = (): (() => void) => {
  const [showPopover, dismissPopover] = useFtux({
    triggerKey: SHOW_FTUX_POST_VIDEO_POPOVER,
    launchDate,
    maxDuration,
  });
  const [showTranscriptTooltip, dismissTranscriptTooltip] = useFtux({
    triggerKey: SHOW_FTUX_NOTIFICATIONS_TRANSCRIPT_TOOLTIP,
    launchDate,
    maxDuration,
  });
  const [showTagTooltip, dismissTagTooltip] = useFtux({
    triggerKey: SHOW_FTUX_SHARE_VIDEO_TAG_TOOLTIP,
    launchDate,
    maxDuration,
  });

  const dismissDestinationFtuxTooltips = () => {
    if (showPopover) {
      dismissPopover();
    }

    if (showTranscriptTooltip) {
      dismissTranscriptTooltip();
    }

    if (showTagTooltip) {
      dismissTagTooltip();
    }
  };

  return dismissDestinationFtuxTooltips;
};

export default useShouldShowOneTimeFtux;
